import API, { API_BASE_URL } from "../../../services/api";

import React, { useState, useEffect } from "react";
import {
  ListGroup,
  Breadcrumb,
  Alert,
  Button,
  Spinner,
  Modal,
  Pagination,
  Form,
  InputGroup,
  Badge,
} from "react-bootstrap";
import {
  FaFolder,
  FaVideo,
  FaFilePdf,
  FaFileAlt,
  FaEye,
  FaHome,
  FaDownload,
  FaYoutube,
  FaPlay,
  FaSearch,
  FaFileArchive,
  FaTh,
  FaList,
} from "react-icons/fa";
import { toast } from "react-toastify";
import StudentBatchService from "../../../services/studentBatchService";

const BatchMaterialsTab = ({ batchId, batchName }) => {
  const [materials, setMaterials] = useState([]);
  const [currentFolder, setCurrentFolder] = useState(null);
  const [folderPath, setFolderPath] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const [currentYoutubeVideo, setCurrentYoutubeVideo] = useState(null);
  const [materialsViewMode, setMaterialsViewMode] = useState("grid");

  // Pagination states
  const [pagination, setPagination] = useState({
    current_page: 1,
    per_page: 20,
    total: 0,
    total_pages: 1,
  });
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Search state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchInput, setSearchInput] = useState("");

  // Folder counts state
  const [folderCounts, setFolderCounts] = useState({});

  useEffect(() => {
    loadMaterials();
  }, [
    batchId,
    currentFolder,
    pagination.current_page,
    itemsPerPage,
    searchTerm,
  ]);

  // Load folder count for batch materials
  const loadFolderCount = async (folderId) => {
    try {
      const response = await StudentBatchService.getBatchFolderCount(folderId);
      return response?.data?.count || 0;
    } catch (error) {
      console.error("Failed to load batch folder count:", error);
      return 0;
    }
  };

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const response = await StudentBatchService.getBatchMaterials(
        batchId,
        currentFolder,
        pagination.current_page,
        itemsPerPage,
        searchTerm,
      );

      let materialsArray = [];
      let paginationData = {};

      if (response && response.data) {
        if (response.data.data && Array.isArray(response.data.data)) {
          materialsArray = response.data.data;
          paginationData = response.data.pagination || {};
        } else if (Array.isArray(response.data)) {
          materialsArray = response.data;
          paginationData = {};
        } else {
          materialsArray = [];
        }
      }

      setMaterials(materialsArray);

      // Update pagination
      if (paginationData.total_pages) {
        setPagination({
          current_page: paginationData.current_page || 1,
          per_page: paginationData.per_page || itemsPerPage,
          total: paginationData.total || 0,
          total_pages: paginationData.total_pages || 1,
        });
      } else {
        // If no pagination from API, calculate based on array length
        setPagination({
          current_page: 1,
          per_page: itemsPerPage,
          total: materialsArray.length,
          total_pages: Math.ceil(materialsArray.length / itemsPerPage),
        });
      }

      // Load folder counts for folder items
      const folders = materialsArray.filter((item) => item.type === "folder");
      const counts = {};
      for (const folder of folders) {
        counts[folder.id] = await loadFolderCount(folder.id);
      }
      setFolderCounts(counts);

      // Load folder path if in a subfolder
      if (currentFolder) {
        await loadFolderPath();
      } else {
        setFolderPath([]);
      }
    } catch (error) {
      console.error("Failed to load batch materials:", error);
      toast.error("Failed to load batch materials");
      setMaterials([]);
    } finally {
      setLoading(false);
    }
  };

  const loadFolderPath = async () => {
    if (!currentFolder) {
      setFolderPath([]);
      return;
    }

    // Build folder path by traversing up from current folder
    // Since we don't have a dedicated API for batch folder path, we'll build it manually
    // This requires us to know parent relationships - for now, we'll just show current folder
    try {
      // Try to get folder info from current materials list
      const currentFolderItem = materials.find((m) => m.id === currentFolder);
      if (currentFolderItem) {
        setFolderPath([{ id: currentFolder, name: currentFolderItem.name }]);
      } else {
        setFolderPath([{ id: currentFolder, name: "Folder" }]);
      }
    } catch (error) {
      console.error("Failed to load folder path:", error);
      setFolderPath([]);
    }
  };

  // Get YouTube video ID from URL
  const getYoutubeVideoId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  // Get YouTube thumbnail URL
  const getYoutubeThumbnail = (url) => {
    const videoId = getYoutubeVideoId(url);
    if (videoId) {
      return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
    }
    return null;
  };

  const handleFolderClick = (folder) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setSearchTerm("");
    setSearchInput("");
    setCurrentFolder(folder.id);
  };

  const handleBreadcrumbClick = (index) => {
    setPagination((prev) => ({ ...prev, current_page: 1 }));
    setSearchTerm("");
    setSearchInput("");
    if (index === -1) {
      setCurrentFolder(null);
    } else if (folderPath && Array.isArray(folderPath) && folderPath[index]) {
      setCurrentFolder(folderPath[index].id);
    }
  };

  const handleViewFile = (file) => {
    const fileUrl = `${API_BASE_URL}/${file.file_path}`;
    window.open(fileUrl, "_blank");
  };

  const playYoutubeVideo = (item) => {
    setCurrentYoutubeVideo(item);
    setShowYoutubeModal(true);
  };

  // Search handlers
  const handleSearch = () => {
    setSearchTerm(searchInput);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearch();
    }
  };

  const handleClearSearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.total_pages) {
      setPagination((prev) => ({ ...prev, current_page: pageNumber }));
    }
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    setItemsPerPage(newLimit);
    setPagination((prev) => ({ ...prev, current_page: 1 }));
  };

  const handleDownloadFile = async (file) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Please login to download");
        return;
      }

      // Get base URL from API config
      let baseURL = API.defaults.baseURL;
      baseURL = baseURL.replace(/\/backend$/, "");

      const downloadUrl = `${baseURL}/backend/batch-materials/download/${file.id}`;

      console.log("Download URL:", downloadUrl);

      // Use fetch with Authorization header
      const response = await fetch(downloadUrl, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Download failed:", errorText);
        toast.error(`Download failed: ${response.status}`);
        return;
      }

      // Get blob from response
      const blob = await response.blob();

      // Get filename from Content-Disposition header if available
      let filename = file.name;
      const contentDisposition = response.headers.get("Content-Disposition");
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success("Download started!");
    } catch (error) {
      console.error("Download failed:", error);
      toast.error("Failed to download file");
    }
  };
  const renderPagination = () => {
    const { current_page, total_pages } = pagination;

    if (total_pages <= 1) return null;

    let items = [];
    const maxVisible = 5;
    let startPage = Math.max(1, current_page - Math.floor(maxVisible / 2));
    let endPage = Math.min(total_pages, startPage + maxVisible - 1);

    if (endPage - startPage + 1 < maxVisible) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    items.push(
      <Pagination.Prev
        key="prev"
        onClick={() => handlePageChange(current_page - 1)}
        disabled={current_page === 1}
      />,
    );

    if (startPage > 1) {
      items.push(
        <Pagination.Item key={1} onClick={() => handlePageChange(1)}>
          1
        </Pagination.Item>,
      );
      if (startPage > 2)
        items.push(<Pagination.Ellipsis key="ellipsis1" disabled />);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === current_page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>,
      );
    }

    if (endPage < total_pages) {
      if (endPage < total_pages - 1)
        items.push(<Pagination.Ellipsis key="ellipsis2" disabled />);
      items.push(
        <Pagination.Item
          key={total_pages}
          onClick={() => handlePageChange(total_pages)}
        >
          {total_pages}
        </Pagination.Item>,
      );
    }

    items.push(
      <Pagination.Next
        key="next"
        onClick={() => handlePageChange(current_page + 1)}
        disabled={current_page === total_pages}
      />,
    );

    return (
      <div className="d-flex justify-content-between align-items-center mt-4">
        <div className="d-flex align-items-center gap-2">
          <Form.Select
            size="sm"
            value={itemsPerPage}
            onChange={handleItemsPerPageChange}
            style={{ width: "auto" }}
          >
            <option value={10}>10 per page</option>
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </Form.Select>
          <span className="text-muted small">
            Showing {(pagination.current_page - 1) * itemsPerPage + 1} -{" "}
            {Math.min(pagination.current_page * itemsPerPage, pagination.total)}{" "}
            of {pagination.total} items
          </span>
        </div>
        <Pagination size="sm">{items}</Pagination>
      </div>
    );
  };

  const getIcon = (type) => {
    if (type === "folder") {
      return <FaFolder className="text-warning me-2" size={20} />;
    }
    if (type === "link") {
      return <FaYoutube className="text-danger me-2" size={20} />;
    }
    if (type === "zip") {
      return <FaFileArchive className="text-success me-2" size={20} />;
    }
    switch (type) {
      case "video":
        return <FaVideo className="text-primary me-2" size={20} />;
      case "pdf":
        return <FaFilePdf className="text-danger me-2" size={20} />;
      default:
        return <FaFileAlt className="text-secondary me-2" size={20} />;
    }
  };

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" variant="primary" size="sm" />
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 materials-title">Batch Materials - {batchName}</h5>
        <div className="view-mode-toggle border rounded bg-white">
          <Button 
            variant={materialsViewMode === 'grid' ? 'primary' : 'light'} 
            className="border-0 rounded-start"
            onClick={() => setMaterialsViewMode('grid')}
          >
            <FaTh />
          </Button>
          <Button 
            variant={materialsViewMode === 'list' ? 'primary' : 'light'} 
            className="border-0 rounded-end"
            onClick={() => setMaterialsViewMode('list')}
          >
            <FaList />
          </Button>
        </div>
      </div>

      {/* Beautiful Search Bar */}
      <div className="mb-4">
        <InputGroup className="materials-search-bar shadow-sm rounded-pill overflow-hidden bg-white border">
          <InputGroup.Text className="bg-transparent border-0 ps-4 text-muted">
            <FaSearch />
          </InputGroup.Text>
          <Form.Control
            placeholder="Search materials by title..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyPress={handleSearchKeyPress}
            className="border-0 py-2 shadow-none"
            style={{ outline: "none", boxShadow: "none" }}
          />
          <Button variant="primary" className="px-4 border-0 rounded-pill m-1" onClick={handleSearch}>
            Search
          </Button>
          {searchTerm && (
            <Button variant="light" className="px-3 border-0 text-danger rounded-pill m-1" onClick={handleClearSearch}>
              Clear
            </Button>
          )}
        </InputGroup>
      </div>

      <Breadcrumb className="mb-4 p-3 bg-light rounded">
        <Breadcrumb.Item
          onClick={() => handleBreadcrumbClick(-1)}
          active={!currentFolder}
          style={{ cursor: "pointer" }}
        >
          <FaHome className="me-1" />
          All Batch Materials
        </Breadcrumb.Item>
        {folderPath &&
          Array.isArray(folderPath) &&
          folderPath.map((folder, index) => (
            <Breadcrumb.Item
              key={folder.id}
              onClick={() => handleBreadcrumbClick(index)}
              active={index === folderPath.length - 1}
              style={{ cursor: "pointer" }}
            >
              {folder.name}
            </Breadcrumb.Item>
          ))}
      </Breadcrumb>

      {materials.length === 0 ? (
        <Alert variant="info">
          <FaFolder className="me-2" />
          {searchTerm
            ? "No batch materials found matching your search."
            : "No batch materials available for this batch yet."}
        </Alert>
      ) : (
        <>
          <div className={`materials-container ${materialsViewMode === 'grid' ? 'materials-grid' : 'materials-list-view'}`}>
            {materials.map((item) => {
              const thumbnail =
                item.type === "link"
                  ? getYoutubeThumbnail(item.url_link)
                  : null;
              return (
                <div
                  key={item.id}
                  className={`material-card ${materialsViewMode}-mode`}
                  style={{
                    cursor: item.type === "folder" ? "pointer" : "default",
                  }}
                  onClick={() =>
                    item.type === "folder" && handleFolderClick(item)
                  }
                >
                  <div className="material-card-content">
                    {item.type === "link" && thumbnail ? (
                      <div className="thumbnail-wrapper">
                        <img
                          src={thumbnail}
                          alt={item.name}
                          className="materials-thumbnail"
                          onError={(e) => {
                            e.target.style.display = "none";
                          }}
                        />
                        <div className="play-overlay"><FaYoutube size={30}/></div>
                      </div>
                    ) : (
                      <div className={`materials-icon icon-${item.type}`}>
                        {getIcon(item.type)}
                      </div>
                    )}
                    <div className="materials-info">
                      <strong className="materials-name text-truncate d-block" title={item.name}>
                        {item.name}
                      </strong>
                      {item.type === "folder" &&
                        folderCounts[item.id] !== undefined && (
                          <Badge bg="secondary" className="mt-1">
                            {folderCounts[item.id]}{" "}
                            {folderCounts[item.id] === 1 ? "item" : "items"}
                          </Badge>
                        )}
                      {item.type === "zip" && (
                        <Badge bg="success" className="mt-1">
                          📦 ZIP
                        </Badge>
                      )}
                      {item.type !== "folder" &&
                        item.type !== "link" &&
                        item.type !== "zip" && (
                          <small className="materials-size text-muted mt-1 d-block">
                            {item.file_size
                              ? `${(item.file_size / 1024).toFixed(2)} KB`
                              : ""}
                          </small>
                        )}
                    </div>
                  </div>

                  <div className="material-actions mt-2 mt-sm-0">
                    {item.type !== "folder" &&
                      (item.type === "link" ? (
                        <Button
                          variant="danger"
                          size="sm"
                          className="materials-watch-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            playYoutubeVideo(item);
                          }}
                        >
                          <FaYoutube className="me-1" /> Watch
                        </Button>
                      ) : item.type === "video" ? (
                        <Button
                          variant="primary"
                          size="sm"
                          className="materials-view-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleViewFile(item);
                          }}
                        >
                          <FaEye className="me-1" /> View
                        </Button>
                      ) : item.type === "zip" ? (
                        <Button
                          variant="success"
                          size="sm"
                          className="materials-download-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownloadFile(item);
                          }}
                        >
                          <FaFileArchive className="me-1" /> Download ZIP
                        </Button>
                      ) : (
                        <div className="d-flex gap-2 justify-content-start">
                          <Button
                            variant="primary"
                            size="sm"
                            className="materials-view-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewFile(item);
                            }}
                          >
                            <FaEye />
                          </Button>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            className="materials-download-btn"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDownloadFile(item);
                            }}
                          >
                            <FaDownload />
                          </Button>
                        </div>
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
          {renderPagination()}
        </>
      )}

      {/* YouTube Player Modal */}
      <Modal
        show={showYoutubeModal}
        onHide={() => setShowYoutubeModal(false)}
        size="lg"
        centered
        className="youtube-modal"
      >
        <Modal.Header closeButton>
          <Modal.Title className="youtube-modal-title">
            <FaYoutube className="text-danger me-2" />
            {currentYoutubeVideo?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          {currentYoutubeVideo && (
            <div className="youtube-embed-container">
              <iframe
                src={`https://www.youtube.com/embed/${getYoutubeVideoId(currentYoutubeVideo.url_link)}?autoplay=1&rel=0&modestbranding=0&showinfo=0&controls=1`}
                className="youtube-iframe"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={currentYoutubeVideo.name}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowYoutubeModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default BatchMaterialsTab;
